import React, { useState, useRef, useEffect } from 'react';
import { articles } from './dataStore';
import { SingleSelectDropdown } from './components/Dropdowns';
import { useNavigate } from 'react-router-dom';
import { Button } from './components/Button';

const FILTERS = [
	{ label: 'All', value: 'all' },
	{ label: 'Keyboard', value: 'keyboard' },
	{ label: 'Switch', value: 'switch' },
	{ label: 'Keycap', value: 'keycap' },
	{ label: 'Misc', value: 'misc' }
];

const STATUS_STYLES = {
	new: { dot: 'bg-blue-500', text: 'text-blue-400' },
	updated: { dot: 'bg-green-500', text: 'text-green-400' },
	outdated: { dot: 'bg-red-500', text: 'text-red-400' }
};

function ArticlesPage() {
	const [selected, setSelected] = useState('all');
	const [search, setSearch] = useState('');
	const [selectedType, setSelectedType] = useState('all');
	const [visibleCount, setVisibleCount] = useState(6);
	const navigate = useNavigate();

	const filtered = articles.filter((a) => {
		const matchesType = selected === 'all' || a.type === selected;
		const matchesSearch = a.title.toLowerCase().includes(search.toLowerCase());
		return matchesType && matchesSearch && a.visible;
	});

	const visibleArticles = filtered.slice(0, visibleCount);
	const hasMore = visibleCount < filtered.length;

	const articleTypeOptions = [
		{ id: 'all', name: 'All Types' },
		{ id: 'keyboard', name: 'Keyboard' },
		{ id: 'switch', name: 'Switch' },
		{ id: 'keycap', name: 'Keycap' },
		{ id: 'misc', name: 'Misc' }
	];

	const filteredArticles = selectedType === 'all'
		? articles
		: articles.filter((a) => a.type === selectedType);

	return (
		<div className='min-h-screen bg-[#111112] py-24 px-4 relative overflow-hidden'>
			{/* Ambient blurred backdrop */}
			<div
				aria-hidden='true'
				className='pointer-events-none select-none fixed inset-0 w-full h-full z-0'
				style={{
					backgroundImage: "url('/pictures/articles_bg.JPG')",
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					filter: 'blur(12px) brightness(0.5)',
					WebkitFilter: 'blur(12px) brightness(0.5)',
					zIndex: 0,
				}}
			/>
			{/* Main content */}
			<div className='relative z-10 w-full mx-auto'>
				<h1 className='text-3xl font-bold text-white mb-6 text-center drop-shadow'>Articles</h1>
				<p className='text-gray-300 mb-12 text-center text-base max-w-2xl mx-auto'>
					Read in-depth reviews, guides, and insights on keyboards, switches, keycaps, and more. Articles will be labed as updated, updating, or outdated depending on their relevancy.
				</p>
				{/* Search bar and filter dropdown */}
				<div className='flex justify-center items-center mb-10 gap-4 w-full min-w-[50vw] max-w-[70vw] mx-auto'>
					<input
						type='text'
						placeholder='Search articles...'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className='w-full bg-black/40 border border-gray-700/60 rounded-2xl px-6 py-2 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-600 text-base shadow-lg backdrop-blur-md transition'
					/>
					<SingleSelectDropdown
						label='Type'
						options={FILTERS.map(f => ({ id: f.value, name: f.label }))}
						selected={selected}
						setSelected={setSelected}
					/>
				</div>
				{/* Article Grid */}
				<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10'>
					{visibleArticles.length === 0 && (
						<p className='col-span-full text-center text-gray-400'>No articles found.</p>
					)}
					{visibleArticles.map((a) => (
						<div
							key={a.id}
							className='group backdrop-blur-lg bg-black/80 border border-gray-900 shadow-xl hover:scale-105 transition transform cursor-pointer rounded overflow-hidden min-h-[340px]'
							onClick={() => {
								if (a.link) navigate(a.link);
								else navigate('/in-progress');
							}}
						>
							{/* Thumbnail */}
							<img
								src={a.thumbnail}
								alt={a.title}
								className='w-full h-[24vh] object-cover rounded-t mb-0 group-hover:after:w-0'
							/>
							<div className='flex-1 flex flex-col p-6'>
								<h2 className='text-lg font-bold text-white mb-1'>
									<span className='relative after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-white after:transition-all after:duration-300 group-hover:after:w-full transition'>{a.title}</span>
								</h2>
								{/* Minimalist status tag below title */}
								{a.status && (
									<div className='flex items-center gap-2 mb-2 mt-1'>
										<span className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_STYLES[a.status].dot}`}></span>
										<span className={`text-xs font-semibold tracking-wide uppercase ${STATUS_STYLES[a.status].text}`}>{a.status}</span>
									</div>
								)}
								<p className='text-gray-300 text-sm mb-2 line-clamp-2'>{a.description}</p>
								<span className='text-xs text-gray-400 mt-auto'>{new Date(a.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
							</div>
						</div>
					))}
				</div>
				{hasMore && (
					<div className='flex justify-center mt-8'>
						<Button onClick={() => setVisibleCount((c) => c + 6)} children="Load More" className="text-white font-semibold px-6 py-2" />
					</div>
				)}
			</div>
		</div>
	);
}

export default ArticlesPage;
